// Local-demo mock API (mock-flagged, spec 8.6 / AC-X-003 "local demo mock-flagged").
// Same surface as the real FastAPI backend so the UI is identical in demo and live.
// Synthetic data only: org "云途智联科技", project "智巡平台 v3" (DESIGN.md 14).
// No real customer data, no real Bridge, no third-party model calls.

import type {
  Attachment,
  CooperationInput,
  CooperationResponse,
  Feedback,
  FeedbackStatus,
  SessionError,
  SessionHandle,
} from './types';
import { LEGAL_TRANSITIONS } from './types';

const ORG = '云途智联科技';
const PROJECT = '智巡平台 v3';
const SESSION_TTL_MS = 1000 * 60 * 30; // 30 min per visit

const now = (offsetMs = 0) => new Date(Date.now() + offsetMs).toISOString();

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

export function sanitizeFilename(name: string): string {
  // AC-B-008: strip path separators / traversal / control chars; keep it displayable.
  const base = name.replace(/[^-\w.一-龥 ]+/g, '_').replace(/^\.+/, '');
  return base.slice(0, 180) || 'attachment';
}

class ApiCallError extends Error {
  status: number;
  code: string;
  requestId: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.requestId = `mock_${Math.random().toString(36).slice(2, 10)}`;
  }
}

function asApiError(e: ApiCallError) {
  return { error: { code: e.code, message: e.message, request_id: e.requestId } };
}

// --- Seed feedback covering every lifecycle state (DESIGN.md 10.8) ---
const seeds: Feedback[] = [
  {
    id: 'fb-1001',
    summary: '地图加载偶发白屏，刷新后恢复',
    detail:
      '在智巡平台 v3 的地图页面，约每两三天会出现一次整屏白屏，控制台报 WebGL context lost。刷新页面后恢复正常。多发生在长时间挂机后。',
    status: 'under_review',
    org: ORG,
    project: PROJECT,
    createdAt: now(-1000 * 60 * 42),
    updatedAt: now(-1000 * 60 * 40),
    timeline: [
      { status: 'submitted', at: now(-1000 * 60 * 42) },
      { status: 'under_review', at: now(-1000 * 60 * 40), note: '我们正在分析反馈' },
    ],
    attachments: [
      { id: 'att-1', name: 'map-white-screen.png', size: 482103, mime: 'image/png' },
    ],
  },
  {
    id: 'fb-1002',
    summary: '巡检报告导出 PDF 缺少页眉',
    detail:
      '导出的巡检报告 PDF 前三页没有页眉，从第四页开始才出现。期望所有页都带页眉与报告编号。',
    status: 'awaiting_info',
    org: ORG,
    project: PROJECT,
    createdAt: now(-1000 * 60 * 60 * 5),
    updatedAt: now(-1000 * 60 * 60 * 4),
    timeline: [
      { status: 'submitted', at: now(-1000 * 60 * 60 * 5) },
      { status: 'under_review', at: now(-1000 * 60 * 60 * 5 + 60000) },
      { status: 'awaiting_info', at: now(-1000 * 60 * 60 * 4) },
    ],
    luoAnswer: {
      body:
        '已收到反馈。为定位页眉缺失范围，请补充：1) 出现问题的报告是通过「单次导出」还是「批量导出」生成的；2) 一份可复现的导出文件或截图。',
      at: now(-1000 * 60 * 60 * 4),
    },
    attachments: [],
  },
  {
    id: 'fb-1003',
    summary: '设备离线告警延迟约 2 分钟',
    detail:
      '设备断网后，平台告警大约 2 分钟后才触发，期望在 30 秒内告警。已确认网络侧无积压。',
    status: 'resolved',
    org: ORG,
    project: PROJECT,
    createdAt: now(-1000 * 60 * 60 * 26),
    updatedAt: now(-1000 * 60 * 60 * 2),
    timeline: [
      { status: 'submitted', at: now(-1000 * 60 * 60 * 26) },
      { status: 'under_review', at: now(-1000 * 60 * 60 * 26 + 90000) },
      { status: 'in_progress', at: now(-1000 * 60 * 60 * 20), note: '已生成变更请求，转入实现侧' },
      { status: 'resolved', at: now(-1000 * 60 * 60 * 2) },
    ],
    luoAnswer: {
      body:
        '已修复：将心跳检测间隔从 120 秒下调到 20 秒，并新增断连快速通道。请确认是否符合预期。',
      at: now(-1000 * 60 * 60 * 2),
    },
    attachments: [
      { id: 'att-2', name: 'alert-delay.log', size: 18432, mime: 'text/plain' },
    ],
  },
  {
    id: 'fb-1004',
    summary: '批量导入设备 CSV 编码异常',
    detail: '批量导入 UTF-8 编码的 CSV 时部分中文设备名乱码，改为 GBK 后正常。',
    status: 'confirmed',
    org: ORG,
    project: PROJECT,
    createdAt: now(-1000 * 60 * 60 * 72),
    updatedAt: now(-1000 * 60 * 60 * 48),
    timeline: [
      { status: 'submitted', at: now(-1000 * 60 * 60 * 72) },
      { status: 'under_review', at: now(-1000 * 60 * 60 * 72 + 120000) },
      { status: 'in_progress', at: now(-1000 * 60 * 60 * 60) },
      { status: 'resolved', at: now(-1000 * 60 * 60 * 50) },
      { status: 'confirmed', at: now(-1000 * 60 * 60 * 48), note: '客户确认关闭' },
    ],
    luoAnswer: {
      body: '已统一编码识别，UTF-8 与 GBK 均可正确导入。感谢确认。',
      at: now(-1000 * 60 * 60 * 48),
    },
    attachments: [],
  },
  {
    id: 'fb-1005',
    summary: '告警规则编辑器保存后丢失条件',
    detail: '复杂告警规则保存后，再次打开时部分条件分支丢失，但告警仍按原规则触发。',
    status: 'reopened',
    org: ORG,
    project: PROJECT,
    createdAt: now(-1000 * 60 * 60 * 30),
    updatedAt: now(-1000 * 60 * 60 * 1),
    timeline: [
      { status: 'submitted', at: now(-1000 * 60 * 60 * 30) },
      { status: 'under_review', at: now(-1000 * 60 * 60 * 30 + 90000) },
      { status: 'in_progress', at: now(-1000 * 60 * 60 * 18) },
      { status: 'resolved', at: now(-1000 * 60 * 60 * 4) },
      { status: 'reopened', at: now(-1000 * 60 * 60 * 1), note: '客户重新打开：问题仍偶发' },
    ],
    luoAnswer: {
      body: '已收到重新打开。将针对条件分支序列化再排查一轮。',
      at: now(-1000 * 60 * 60 * 1),
    },
    attachments: [],
  },
  {
    id: 'fb-1006',
    summary: '登录页在 iOS 16 输入框被键盘遮挡',
    detail: 'iPhone 上登录页输入账号时，软键盘弹出后会遮挡密码输入框，无法看到输入内容。',
    status: 'submitted',
    org: ORG,
    project: PROJECT,
    createdAt: now(-1000 * 60 * 8),
    updatedAt: now(-1000 * 60 * 8),
    timeline: [{ status: 'submitted', at: now(-1000 * 60 * 8) }],
    attachments: [],
  },
  {
    id: 'fb-1007',
    summary: '地图标记聚合在低缩放层级卡顿',
    detail: '地图缩放到城市级时，数千个标记聚合导致明显掉帧，操作不流畅。',
    status: 'in_progress',
    org: ORG,
    project: PROJECT,
    createdAt: now(-1000 * 60 * 60 * 12),
    updatedAt: now(-1000 * 60 * 60 * 3),
    timeline: [
      { status: 'submitted', at: now(-1000 * 60 * 60 * 12) },
      { status: 'under_review', at: now(-1000 * 60 * 60 * 12 + 60000) },
      { status: 'in_progress', at: now(-1000 * 60 * 60 * 3), note: '已生成变更请求，转入实现侧' },
    ],
    luoAnswer: {
      body: '已生成变更请求，转入实现侧。预计引入按视口聚合与 WebGL 渲染优化。',
      at: now(-1000 * 60 * 60 * 3),
    },
    attachments: [],
  },
];

const store: Feedback[] = seeds.map((f) => ({ ...f, timeline: [...f.timeline], attachments: [...f.attachments] }));

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v));
}

function requireTransition(from: FeedbackStatus, to: FeedbackStatus) {
  const legal = LEGAL_TRANSITIONS[from];
  if (!legal.includes(to)) {
    throw new ApiCallError(409, 'invalid_transition', `不能从 ${from} 转换到 ${to}`);
  }
}

export const mockApi = {
  async createCooperation(input: CooperationInput): Promise<CooperationResponse> {
    await sleep(320);
    void input;
    return { status: 'submitted' };
  },

  async bootstrapSession(token: string): Promise<SessionHandle> {
    await sleep(280);
    if (!token) throw new ApiCallError(401, 'invalid_token', '链接无效');
    const lower = token.toLowerCase();
    if (lower === 'expired' || lower.startsWith('expired')) {
      throw new ApiCallError(410, 'expired_token', '链接已过期');
    }
    if (lower === 'revoked' || lower.startsWith('revoked')) {
      throw new ApiCallError(410, 'revoked_token', '链接已被撤销');
    }
    // 'demo', 'valid*', or any other non-empty token -> valid (mock).
    return {
      org: ORG,
      project: PROJECT,
      expiresAt: new Date(Date.now() + SESSION_TTL_MS).toISOString(),
    };
  },

  async listFeedback(): Promise<Feedback[]> {
    await sleep(180);
    return clone(store).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  },

  async getFeedback(id: string): Promise<Feedback> {
    await sleep(160);
    const f = store.find((x) => x.id === id);
    if (!f) throw new ApiCallError(404, 'not_found', '反馈不存在');
    return clone(f);
  },

  async createFeedback(input: {
    summary: string;
    detail: string;
    files: { name: string; size: number; type: string }[];
  }): Promise<Feedback> {
    await sleep(320);
    const id = `fb-${Date.now().toString(36)}`;
    const attachments: Attachment[] = input.files.map((f, i) => ({
      id: `att-${Date.now().toString(36)}-${i}`,
      name: sanitizeFilename(f.name),
      size: f.size,
      mime: f.type,
    }));
    const fb: Feedback = {
      id,
      summary: input.summary,
      detail: input.detail,
      status: 'submitted',
      org: ORG,
      project: PROJECT,
      createdAt: now(),
      updatedAt: now(),
      timeline: [{ status: 'submitted', at: now() }],
      attachments,
    };
    store.unshift(fb);
    // Simulate the Luo judgment engine (default-off third-party egress, rule-based).
    // submitted -> under_review -> awaiting_info (deterministic demo of more-info).
    setTimeout(() => {
      fb.status = 'under_review';
      fb.updatedAt = now();
      fb.timeline.push({ status: 'under_review', at: now(), note: '我们正在分析反馈' });
    }, 1500);
    setTimeout(() => {
      fb.status = 'awaiting_info';
      fb.updatedAt = now();
      fb.timeline.push({ status: 'awaiting_info', at: now() });
      fb.luoAnswer = {
        body: '已收到反馈。为加快定位，请补充：问题出现的频次、可复现的步骤，以及一张相关截图或日志。',
        at: now(),
      };
    }, 4000);
    return clone(fb);
  },

  async submitMoreInfo(id: string, reply: string): Promise<Feedback> {
    await sleep(260);
    const f = store.find((x) => x.id === id);
    if (!f) throw new ApiCallError(404, 'not_found', '反馈不存在');
    requireTransition(f.status, 'under_review');
    f.status = 'under_review';
    f.updatedAt = now();
    f.timeline.push({ status: 'under_review', at: now(), note: `补充信息：${reply}` });
    return clone(f);
  },

  async reopenFeedback(id: string): Promise<Feedback> {
    await sleep(240);
    const f = store.find((x) => x.id === id);
    if (!f) throw new ApiCallError(404, 'not_found', '反馈不存在');
    requireTransition(f.status, 'reopened');
    f.status = 'reopened';
    f.updatedAt = now();
    f.timeline.push({ status: 'reopened', at: now(), note: '客户重新打开' });
    return clone(f);
  },

  async confirmResolved(id: string): Promise<Feedback> {
    await sleep(240);
    const f = store.find((x) => x.id === id);
    if (!f) throw new ApiCallError(404, 'not_found', '反馈不存在');
    requireTransition(f.status, 'confirmed');
    f.status = 'confirmed';
    f.updatedAt = now();
    f.timeline.push({ status: 'confirmed', at: now(), note: '客户确认关闭' });
    return clone(f);
  },
};

export { ApiCallError, asApiError };
export type { SessionError };
